import { cloneElement, ReactElement } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useData } from '../contexts/data-context/use-data';
import { useUserInfo } from '../contexts/user-context/use-user-info';
import Loading from '../design-system/loading/loading';

interface ProtectedRouteProps {
  element: ReactElement;
  requiresCompleteSignup?: boolean;
}

// TODO: do we need requiresCompleteSignup?
export const ProtectedRoute = ({ element, requiresCompleteSignup = true }: ProtectedRouteProps) => {
  const { isAuthenticated, authenticatedInfo } = useUserInfo();
  const location = useLocation();
  const { data: entityData, isLoading: isEntityDataLoading } = useData();

  if (!isAuthenticated) {
    // Check localStorage for last logged-in user's email
    const lastUserEmail = localStorage.getItem('lastUserEmail');
    const loginUrl = lastUserEmail ? `/auth/login?login_hint=${encodeURIComponent(lastUserEmail)}` : '/auth/login';

    // Using window.location.href to avoid redirect loop and multi-tab logout
    window.location.href = loginUrl;

    return null;
  }

  if (!authenticatedInfo?.emailVerified && location.pathname !== '/signup/verifyemail') {
    return <Navigate to="/signup/verifyemail" replace />;
  }

  if (requiresCompleteSignup && !authenticatedInfo?.isOrgReady) {
    return <Navigate to="/signup/organisation" replace />;
  }

  if (isEntityDataLoading) {
    return <Loading text="Loading..." />;
  }

  if (location.pathname === '/signup/organisation' && authenticatedInfo?.isOrgReady) {
    // If the user has no accounts, we should redirect them to the open seapoint account page after competing signup flow
    if (entityData?.accounts.length === 0) {
      return <Navigate to="/accounts/open-seapoint-account" replace />;
    }

    return <Navigate to="/" replace />;
  }

  return cloneElement(element, { children: <Outlet /> });
};
